import { HttpErrorResponse } from '@angular/common/http';

/**
 * The backend's subscription quota rejections (tokens per day/month, storage, agents, data sources).
 * They come back as HTTP 400 (route gates) or 409 (runnable gate), and some endpoints read the body
 * as text, so the body may be an object or a JSON string.
 *
 * Each carries a stable `code` (`QUOTA_*`, `SAI_Backend/src/utils/subscription-gates.ts`), which is
 * what the copy is chosen by. The returned string is a translation key, present in `en.json` and
 * `ar.json`: the alert service localises it, so the toast follows the user's language. Before the
 * code existed the raw English message was shown as is, in English for everyone. An older backend
 * that sends no code is still recognised by its message text.
 */
export const QUOTA_MESSAGE_KEYS: Readonly<Record<string, string>> = {
    QUOTA_TOKENS_DAILY: 'Maximum Token usage limit reached for your current package for today.',
    QUOTA_TOKENS_MONTHLY: 'Maximum Token usage limit reached for your current package for this month.',
    QUOTA_STORAGE: 'Maximum Storage limit reached for your current package.',
    QUOTA_AGENTS: 'Maximum agent limit reached for your current package.',
    QUOTA_DATA_SOURCES: 'Maximum data source limit reached for your current package.',
};

const QUOTA_MESSAGE = /^Maximum .+ limit reached for your current package/i;

const bodyOf = (raw: unknown): { message?: unknown; code?: unknown } | null => {
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw) as { message?: unknown; code?: unknown } | null;
        } catch {
            return { message: raw };
        }
    }
    return raw as { message?: unknown; code?: unknown } | null;
};

/** The translation key for the quota rejection carried by `error`, or null for any other failure. */
export function quotaErrorMessage(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse) || (error.status !== 400 && error.status !== 409)) {
        return null;
    }
    const body = bodyOf(error.error);
    const code = typeof body?.code === 'string' ? body.code : null;
    if (code && QUOTA_MESSAGE_KEYS[code]) {
        return QUOTA_MESSAGE_KEYS[code];
    }
    const message = typeof body?.message === 'string' ? body.message.trim() : '';
    if (!QUOTA_MESSAGE.test(message)) {
        return null;
    }
    // The known messages are keys themselves; an unknown quota wording still reaches the user verbatim.
    return message;
}
